import "react-international-phone/style.css";

import { randomUUID } from "node:crypto";

import {
  Alert,
  Anchor,
  Button,
  Container,
  Input,
  NativeSelect,
  Paper,
  Stack,
  Text,
  TextInput,
  ThemeIcon,
  Title,
} from "@mantine/core";
import {
  ActionFunctionArgs,
  json,
  LoaderFunctionArgs,
  MetaFunction,
} from "@remix-run/node";
import {
  Form,
  Link,
  useActionData,
  useLoaderData,
  useNavigation,
} from "@remix-run/react";
import { IconCheck } from "@tabler/icons-react";
import type { AxiosRequestConfig } from "axios";
import { useState } from "react";
import { PhoneInput } from "react-international-phone";
import { z } from "zod";

import { PAGE_TITLE } from "~/constants";
import { useTranslation } from "~/hooks/use-typed-translation";
import i18nextServer from "~/localization/i18n.server";
import translationEN from "~/localization/locales/en/translation.json";
import translationFR from "~/localization/locales/fr/translation.json";
import { api } from "~/utils/api.server";

import { ProfessionalRegistration } from "~/features/professional-registration";
import { referralContext, registrationAvailability, registerProfessional, type JoinActionData } from "~/features/professional-registration/registration.server";
import classes from "./join.module.scss";

const schema = z.object({
  referralToken: z.string().trim().min(6).max(500),
  submissionId: z.string().min(16).max(80),
  accountType: z.enum(["doctor", "organization"]),
  name: z.string().trim().min(2).max(180),
  phoneNumber: z
    .string()
    .trim()
    .max(40)
    .regex(/^\+?[0-9\s().-]+$/)
    .refine(value => {
      const digits = value.replace(/\D/g, "");
      return digits.length >= 8 && digits.length <= 15;
    }),
  email: z.string().email().optional().or(z.literal("")),
  specialty: z.string().trim().max(180).optional(),
  channel: z.string().optional(),
  website: z.string().max(200).optional(),
});

const allowedChannels = new Set([
  "whatsapp",
  "instagram",
  "messenger",
  "facebook",
  "copy_link",
  "other",
  "unknown",
]);

const getRouteFixedT = (request: Request) =>
  i18nextServer.getFixedT(request, "translation", {
    resources: {
      en: { translation: translationEN },
      fr: { translation: translationFR },
    },
  });

const referralErrorTranslations: Record<string, string> = {
  referral_invalid: "referral-join-invalid-error",
  referral_unavailable: "referral-join-unavailable-error",
  referral_invalid_phone: "referral-join-phone-error",
  referral_invalid_submission: "referral-join-submission-error",
  referral_not_eligible: "referral-join-not-eligible-error",
  referral_submission_conflict: "referral-join-conflict-error",
  referral_submission_incomplete: "referral-join-submission-error",
  referral_rate_limited: "referral-join-rate-limit-error",
};

const getReferralErrorCode = (error: any): string | null => {
  const detail = error.response?.data?.detail;
  if (detail && typeof detail === "object" && !Array.isArray(detail)) {
    return typeof detail.code === "string" ? detail.code : null;
  }
  return typeof detail === "string" && referralErrorTranslations[detail]
    ? detail
    : null;
};

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  const title =
    data?.socialTitle || `You’ve been invited to join ${PAGE_TITLE}`;
  const description =
    data?.socialDescription ||
    "Join our growing network of healthcare professionals, build your digital practice, and connect with more patients.";
  const image = data
    ? `${data.origin}/og/referral-join-${data.locale === "fr" ? "fr" : "en"}-v2.jpg`
    : undefined;

  return [
    { title },
    { name: "description", content: description },
    { property: "og:title", content: title },
    { property: "og:description", content: description },
    { property: "og:type", content: "website" },
    { property: "og:url", content: data?.socialUrl },
    { property: "og:site_name", content: PAGE_TITLE },
    {
      property: "og:locale",
      content: data?.locale === "fr" ? "fr_FR" : "en_GB",
    },
    { property: "og:image", content: image },
    { property: "og:image:secure_url", content: image },
    { property: "og:image:type", content: "image/jpeg" },
    { property: "og:image:width", content: "1200" },
    { property: "og:image:height", content: "630" },
    { property: "og:image:alt", content: data?.socialImageAlt },
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:title", content: title },
    { name: "twitter:description", content: description },
    { name: "twitter:image", content: image },
    { name: "twitter:image:alt", content: data?.socialImageAlt },
    { name: "robots", content: "noindex, nofollow" },
    {
      tagName: "link",
      rel: "canonical",
      href: data?.canonicalUrl,
    },
  ];
};

export const loader = async ({ request }: LoaderFunctionArgs) => {
  const url = new URL(request.url);
  const t = await getRouteFixedT(request);
  const locale = await i18nextServer.getLocale(request);
  const referral = referralContext(request);
  const selfRegistration = referral.hasReferral ? null : await registrationAvailability();
  const requestedChannel = url.searchParams.get("channel") || "unknown";

  return json({
    referralToken: referral.token,
    hasReferral: referral.hasReferral,
    selfRegistration,
    channel: allowedChannels.has(requestedChannel)
      ? requestedChannel
      : "unknown",
    submissionId: randomUUID(),
    metaTitle: t("referral-join-meta-title"),
    metaDescription: t("referral-join-meta-description"),
    socialTitle: t(referral.hasReferral ? "referral-join-social-title" : "professional-join-title"),
    socialDescription: t(referral.hasReferral ? "referral-join-social-description" : "professional-join-introduction"),
    socialImageAlt: t("referral-join-social-image-alt"),
    socialUrl: url.toString(),
    canonicalUrl: `${url.origin}/${locale}/join`,
    origin: url.origin,
    locale,
  }, { headers: { "Cache-Control": "private, no-store" } });
};

export const action = async ({ request }: ActionFunctionArgs) => {
  const t = await getRouteFixedT(request);
  const locale = await i18nextServer.getLocale(request);
  const values = Object.fromEntries(await request.formData());
  if (!referralContext(request).hasReferral) return registerProfessional(values, t, locale);
  const parsed = schema.safeParse(values);
  const submissionId =
    typeof values.submissionId === "string" ? values.submissionId : "";
  if (!parsed.success) {
    return json<JoinActionData>(
      {
        ok: false,
        message: t("referral-join-required-error"),
        submissionId,
      },
      { status: 400 }
    );
  }
  try {
    const requestConfig = {
      headers: { "Accept-Language": locale },
      maxRetries: 0,
    } satisfies AxiosRequestConfig & { maxRetries: number };

    await api.post("/account-management/referrals/attribute", {
      referral_code: parsed.data.referralToken,
      account_type: parsed.data.accountType,
      name: parsed.data.name,
      phone_number: parsed.data.phoneNumber,
      email: parsed.data.email || undefined,
      specialty: parsed.data.specialty || undefined,
      channel: allowedChannels.has(parsed.data.channel || "")
        ? parsed.data.channel
        : "unknown",
      submission_id: parsed.data.submissionId,
      website: parsed.data.website || undefined,
    }, requestConfig);
    return json<JoinActionData>({
      ok: true,
      message: t("referral-join-success-message"),
      submissionId: parsed.data.submissionId,
    });
  } catch (error: any) {
    const errorCode = getReferralErrorCode(error);
    return json<JoinActionData>(
      {
        ok: false,
        message: t(
          errorCode
            ? referralErrorTranslations[errorCode]
            : "referral-join-submission-error"
        ),
        submissionId: parsed.data.submissionId,
      },
      { status: error.response?.status || 500 }
    );
  }
};

export default function ReferralJoinRoute() {
  const { t } = useTranslation();
  const { referralToken, channel, submissionId, hasReferral, selfRegistration } = useLoaderData<typeof loader>();
  const actionData = useActionData<typeof action>();
  const navigation = useNavigation();
  const complete = actionData?.ok === true;
  const [phoneNumber, setPhoneNumber] = useState("");
  const [accountType, setAccountType] = useState<"doctor" | "organization">(
    "doctor"
  );

  return (
    <Container size={560} py={{ base: 40, sm: 72 }}>
      <Stack align="center" mb="xl">
        <Text c="violet" fw={700}>
          LA RUCHE HEALTH
        </Text>
        <Title order={1} ta="center">
          {t(hasReferral ? "referral-join-title" : "professional-join-title")}
        </Title>
        <Text ta="center" c="dimmed">
          {t(hasReferral ? "referral-join-introduction" : "professional-join-introduction")}
        </Text>
      </Stack>
      <Paper withBorder shadow="sm" radius="lg" p={{ base: "lg", sm: "xl" }}>
        {hasReferral && !referralToken ? (
          <Alert color="red">{t("referral-join-incomplete-link")}</Alert>
        ) : complete ? (
          <Stack align="center" gap="md" py={{ base: "lg", sm: "xl" }}>
            <ThemeIcon color="green" variant="light" radius="xl" size={72}>
              <IconCheck size={38} stroke={2.5} />
            </ThemeIcon>
            <Title order={2} ta="center">
              {t("referral-join-application-received")}
            </Title>
            <Text ta="center" fw={500}>
              {t("referral-join-details-sent")}
            </Text>
            <Text ta="center" c="dimmed" maw={440}>
              {t("referral-join-review-message")}
            </Text>
            <Text ta="center" c="dimmed" size="sm">
              {t("referral-join-close-page")}
            </Text>
          </Stack>
        ) : !hasReferral ? (
          (selfRegistration === "enabled" || actionData?.step) ? <ProfessionalRegistration submissionId={submissionId} result={actionData} />
            : <Stack><Alert color="orange">{t(selfRegistration === "disabled" ? "professional-join-disabled" : "professional-join-unavailable")}</Alert>
                {selfRegistration === "unavailable" && <Anchor href="">{t("professional-join-retry")}</Anchor>}</Stack>
        ) : (
          <Form method="post">
            <Stack>
              {actionData?.message && (
                <Alert color="red">{actionData.message}</Alert>
              )}
              <input type="hidden" name="referralToken" value={referralToken} />
              <input type="hidden" name="channel" value={channel} />
              <input
                type="hidden"
                name="submissionId"
                value={actionData?.submissionId || submissionId}
              />
              <div
                aria-hidden="true"
                style={{ position: "absolute", left: "-10000px" }}>
                <label htmlFor="referral-website">Website</label>
                <input
                  id="referral-website"
                  name="website"
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                />
              </div>
              <NativeSelect
                required
                name="accountType"
                label={t("referral-join-account-type")}
                value={accountType}
                onChange={(event) =>
                  setAccountType(
                    event.currentTarget.value as "doctor" | "organization"
                  )
                }
                data={[
                  {
                    value: "doctor",
                    label: t("referral-join-practitioner"),
                  },
                  {
                    value: "organization",
                    label: t("referral-join-organization"),
                  },
                ]}
              />
              <TextInput
                required
                name="name"
                autoComplete={accountType === "doctor" ? "name" : "organization"}
                maxLength={180}
                label={
                  accountType === "doctor"
                    ? t("referral-join-full-name")
                    : t("referral-join-organization-name")
                }
                placeholder={
                  accountType === "doctor"
                    ? t("referral-join-doctor-name-placeholder")
                    : t("referral-join-organization-name-placeholder")
                }
              />
              <Input.Wrapper
                label={t("referral-join-phone-number")}
                required>
                <PhoneInput
                  className={classes["react-international-phone"]}
                  inputClassName={classes["react-international-phone-input"]}
                  countrySelectorStyleProps={{
                    className:
                      classes["react-international-phone-country-selector"],
                  }}
                  defaultCountry="ci"
                  name="phoneNumber"
                  required
                  value={phoneNumber}
                  onChange={setPhoneNumber}
                  inputProps={{
                    autoComplete: "tel",
                    inputMode: "tel",
                    maxLength: 40,
                  }}
                />
              </Input.Wrapper>
              <TextInput
                name="email"
                type="email"
                autoComplete="email"
                label={t("referral-join-email")}
                placeholder="doctor@example.com"
              />
              <TextInput
                name="specialty"
                maxLength={180}
                label={
                  accountType === "doctor"
                    ? t("referral-join-specialty")
                    : t("referral-join-organization-type")
                }
                placeholder={
                  accountType === "doctor"
                    ? t("referral-join-specialty-placeholder")
                    : t("referral-join-organization-type-placeholder")
                }
              />
              <TextInput
                label={t("referral-join-referral-code")}
                value={referralToken}
                disabled
              />
              <Button
                type="submit"
                mt="md"
                loading={navigation.state === "submitting"}
              >
                {t("referral-join-submit")}
              </Button>
              <Text fz={12} c="dimmed" ta="center">
                {t("referral-join-privacy-prefix")}{" "}
                <Anchor
                  component={Link}
                  target="_blank"
                  rel="noreferrer"
                  to="https://docs.google.com/document/d/1amefCfTGw-erveo2ILEYqe_Ppvb0VXYDjjq0lE32f1A/edit?usp=sharing"
                  inherit>
                  {t("referral-join-privacy-link")}
                </Anchor>
                .
              </Text>
            </Stack>
          </Form>
        )}
      </Paper>
    </Container>
  );
}
